import { useEffect } from 'react';
import { pm2SitemapStore, findPageDescriptorByPath, findCourseDescriptorFromParents, getPageDescriptorByPath } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useNavigationManager, useLocation } from '@provider/navigation';
import { courseRevisionsStore } from './store.js';
import { disabledIdsToKey } from './util.disabled-ids-to-key.js';
import { isDescriptorInsideCourse } from './util.is-inside-course.js';
import { useLeaveRevision } from './interactions/leave.js';

function useCourseRevisionNavigationInterceptor() {
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  const sitemapState = pm2SitemapStore.useSelector(state => state);
  const navigation = useNavigationManager();
  const location = useLocation();
  const pageDescriptor = findPageDescriptorByPath(sitemapState, location.pathname);
  const courseDescriptor = findCourseDescriptorFromParents(sitemapState, pageDescriptor);
  const leaveRevision = useLeaveRevision(courseDescriptor);

  // Ideally we wish to display a prompt when users attempts to leave a course
  // using default navigation, in case they are still in edit mode.
  useEffect(() => {
    const unsubscribe = navigation.addInterceptor("course-revisions/leave", async (cancelToken, nextHref) => {
      const state = courseRevisionsStore.getState();
      if (!state.isEditing) {
        return;
      }
      const nextPageDescriptor = await getPageDescriptorByPath(configStore, sitemapStore, nextHref);
      const nextCourseDescriptor = findCourseDescriptorFromParents(sitemapState, nextPageDescriptor);
      if (!nextCourseDescriptor || nextCourseDescriptor.id !== courseDescriptor?.id || !isDescriptorInsideCourse(sitemapState, nextPageDescriptor)) {
        const result = await leaveRevision();

        // In case the user did not explictly decide what to do with their
        // current revision, then prevent their navigation and force them to
        // remain within the course.
        if (result === "cancel") {
          cancelToken.cancel();
        }
      }
    });

    // However, also ensure that they are prompted if they attempt to close the
    // page while still in edit mode.
    const onUnload = evt => {
      const state = courseRevisionsStore.getState();
      if (!state.isEditing) {
        return;
      }
      const hasChanges = disabledIdsToKey(state.disabledIds) !== disabledIdsToKey(state.editableRevision?.disabledContentIds);
      if (!hasChanges) {
        return;
      }
      return evt.returnValue = "Du har ændringer som endnu ikke er blevet gemt. Vil du fortsætte?";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      unsubscribe();
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [configStore, courseDescriptor?.id, leaveRevision, navigation, sitemapState, sitemapStore]);

  // In case the user managed to leave the Course without triggering the above
  // guards (e.g. by using the browser back button), then ensure the
  // leavePrompt is triggered such that edit-mode can be properly finsihed.
  useEffect(() => {
    setTimeout(() => {
      const {
        isEditing
      } = courseRevisionsStore.getState();
      if (isEditing && pageDescriptor?.type !== "Course" && !isDescriptorInsideCourse(sitemapState, pageDescriptor)) {
        leaveRevision({
          requiresInteraction: true
        });
      }
    });
  }, [pageDescriptor, leaveRevision, sitemapState]);
}

export { useCourseRevisionNavigationInterceptor };
//# sourceMappingURL=hook.navigation-interceptor.js.map
